import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { Table, Badge } from "react-bootstrap";
import { fetchAllDataByIDAPI } from "../../utils/api";
import { useParams } from "react-router-dom";
import withAuth from "../../utils/withAuth";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const pct = (n) => `${Number(n || 0).toFixed(2)}%`;

const SkeletonRow = ({ cols = 4 }) => (
    <tr>
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i}>
                <div className="skel-line" />
            </td>
        ))}
    </tr>
);

const CenterHeadPerformance = () => {
    const { slug } = useParams();
    const [rows, setRows] = useState([]);
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        getCenterHeadPerformance(slug);
    }, [slug]);

    const getCenterHeadPerformance = async (slug) => {
        try {
            setLoading(true);
            const res = await fetchAllDataByIDAPI(`hr-center-head-performance/${slug}`);
            if (res?.data?.status === 1 && Array.isArray(res.data.data)) {
                setRows(res.data.data);
            } else {
                setRows([]);
            }
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const totalSummary = useMemo(() => {
        if (!rows.length) return null;
        const sum = (arr, key) =>
            arr.reduce((a, r) => a + Number(r[key] || 0), 0);
        const inboundDelivered = sum(rows, "inbound_delivered");
        const inboundTotal = sum(rows, "inbound_total_orders");
        const outboundDelivered = sum(rows, "outbound_delivered");
        const outboundTotal = sum(rows, "outbound_total_orders");

        return {
            inbound_last15_amount: sum(rows, "inbound_last15_amount"),
            inbound_target_amount: sum(rows, "inbound_target_amount"),
            inbound_delivery_percent:
                inboundTotal > 0 ? ((inboundDelivered / inboundTotal) * 100) : 0,
            outbound_last15_amount: sum(rows, "outbound_last15_amount"),
            outbound_target_amount: sum(rows, "outbound_target_amount"),
            outbound_delivery_percent:
                outboundTotal > 0 ? ((outboundDelivered / outboundTotal) * 100) : 0,
        };
    }, [rows]);

    const targetMeta = (actual, target) => {
        const a = Number(actual || 0);
        const t = Number(target || 0);

        if (t <= 0) {
            return {
                pctOfTarget: 0,
                barNow: 0,
                label: "No target set",
                badge: "N/A",
                variant: "secondary",
            };
        }

        const pctOfTarget = (a / t) * 100;           // can exceed 100
        const barNow = Math.max(0, Math.min(100, pctOfTarget)); // clamp to 0–100 for the bar
        let label = `${pctOfTarget.toFixed(1)}% of target`;
        let badge = "Behind";
        let variant = "danger";

        if (pctOfTarget >= 100) {
            badge = "Target Met";
            variant = "success";
            label = `Target met (+${(pctOfTarget - 100).toFixed(1)}%)`;
        } else if (pctOfTarget >= 80) {
            badge = "Near";
            variant = "warning";
            label = `${pctOfTarget.toFixed(1)}% of target (almost there)`;
        } else if (pctOfTarget >= 50) {
            badge = "Progressing";
            variant = "info";
            label = `${pctOfTarget.toFixed(1)}% of target`;
        }

        return { pctOfTarget, barNow, label, badge, variant };
    };

    return (
        <div className="employeepage">
            <Helmet>
                <title>SATKARTAR::HR::CENTER PERFORMANCE</title>
            </Helmet>

            {/* Minimal CSS for skeleton shimmer */}
            <style>{`
        .skel-line {
          height: 14px;
          width: 100%;
          border-radius: 6px;
          background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
          background-size: 400% 100%;
          animation: skel 1.4s ease-in-out infinite;
        }
        @keyframes skel { 0% {background-position: 100% 0} 100% {background-position: 0 0} }
        .metric { display:flex; flex-direction:column; gap:10px; }
        .muted { font-size: 12px; opacity: 1; }
      `}</style>

            <ContentWrapper>
                <div className="card employeepage" style={{ marginBottom: "55px" }}>
                    <h5 className="mb-0 text-uppercase"><b>Center Head Performance (Inbound / Outbound)</b></h5>
                    <div className="table pagetable table-responsive">
                        <Table className="table-sm align-middle" responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>#</th>
                                    <th>Cluster Head</th>
                                    <th style={{ minWidth: 300 }}>Inbound (Last 15 days & Delivery)</th>
                                    <th style={{ minWidth: 300 }}>Outbound (Last 15 days & Delivery)</th>
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    Array.from({ length: 8 }).map((_, idx) => (
                                        <SkeletonRow key={idx} cols={4} />
                                    ))
                                ) : rows.length ? (
                                    rows.map((r, i) => (
                                        <tr key={r.center_id || i}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <div className="metric">
                                                    <div>{r.center_name}</div>
                                                </div>
                                            </td>

                                            {/* Inbound */}
                                            <td>
                                                <div className="metric">
                                                    <div>
                                                        {(() => {
                                                            const m = targetMeta(r.inbound_last15_amount, r.inbound_target_amount);
                                                            return (
                                                                <>
                                                                    <b>Sales 15d:</b> <span className="muted">{m.label}</span>{" "}
                                                                    <Badge bg={m.variant}>{m.badge}</Badge>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                    <div>
                                                        <b>Delivery 2m:</b> {pct(r.inbound_delivery_percent)}{" "}
                                                        <span className="muted">(goal ≥ 48%)</span>{" "}
                                                        {r.inbound_delivery_target_met ? (
                                                            <Badge bg="success">On Track</Badge>
                                                        ) : (
                                                            <Badge bg="danger">Below</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Outbound */}
                                            <td>
                                                <div className="metric">
                                                    <div>
                                                        {(() => {
                                                            const m = targetMeta(r.outbound_last15_amount, r.outbound_target_amount);
                                                            return (
                                                                <>
                                                                    <b>Sales 15d:</b> <span className="muted">{m.label}</span>{" "}
                                                                    <Badge bg={m.variant}>{m.badge}</Badge>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                    <div>
                                                        <b>Delivery 2m:</b> {pct(r.outbound_delivery_percent)}{" "}
                                                        <span className="muted">(goal ≥ 45%)</span>{" "}
                                                        {r.outbound_delivery_target_met ? (
                                                            <Badge bg="success">On Track</Badge>
                                                        ) : (
                                                            <Badge bg="danger">Below</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            <tfoot>
                                <tr>
                                    <th colSpan={2}>Totals</th>
                                    <th>
                                        {totalSummary ? (
                                            <>
                                                {(() => {
                                                    const m = targetMeta(totalSummary.inbound_last15_amount, totalSummary.inbound_target_amount);
                                                    return (
                                                        <>
                                                            <div><b>Sales 15d:</b> <span className="muted">{m.label}</span> </div>
                                                            <div style={{ marginTop: 6 }}></div>
                                                        </>
                                                    );
                                                })()}
                                                <div><b>Delivery 2m:</b> {pct(totalSummary.inbound_delivery_percent)}</div>
                                            </>
                                        ) : (
                                            <div className="muted">—</div>
                                        )}
                                    </th>
                                    <th>
                                        {totalSummary ? (
                                            <>
                                                {(() => {
                                                    const m = targetMeta(totalSummary.outbound_last15_amount, totalSummary.outbound_target_amount);
                                                    return (
                                                        <>
                                                            <div><b>Sales 15d:</b> <span className="muted">{m.label}</span> </div>
                                                            <div style={{ marginTop: 6 }}></div>
                                                        </>
                                                    );
                                                })()}
                                                <div><b>Delivery 2m:</b> {pct(totalSummary.outbound_delivery_percent)}</div>
                                            </>
                                        ) : (
                                            <div className="muted">—</div>
                                        )}
                                    </th>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(CenterHeadPerformance);
